import Api from '../api';

export interface UserCenterUser {
  id: number;
  nome: string;
  email: string;
  ativo: boolean;
}

export interface UserActionResponse {
  success: boolean;
  message: string;
  action:
    | 'user_created'
    | 'user_updated'
    | 'user_deactivated'
    | 'ticket_created'
    | 'chat_suggested'
    | 'error';
  // chat_suggested devolve só o chat_context (mensagem de abertura do chat).
  data?: Partial<UserCenterUser> & { chat_context?: string };
}

export interface UserFormPayload {
  nome: string;
  email?: string;
  ativo?: boolean;
  ddd_celular?: string;
  numero_celular?: string;
  ddd_celular_adicional1?: string;
  numero_celular_adicional1?: string;
  ddd_telefone?: string;
  numero_telefone?: string;
  ramal_telefone?: string;
  cargo_id?: number;
  sexo_id?: number;
  filter_ids?: number[];
  // true = chat sem atendente disponível; o back abre protocolo como antes.
  fallback_ticket?: boolean;
}

export interface UserFormOption {
  id: number;
  nome: string;
}

export interface UserFormOptionsResponse {
  success: boolean;
  roles: UserFormOption[];
  genres: UserFormOption[];
  filters: UserFormOption[];
}

export const createUser = async (payload: UserFormPayload) => {
  const response = await Api.post<UserActionResponse>('/boletim_web/base/users', payload);
  return response;
};

export const updateUser = async (id: number, payload: Partial<UserFormPayload>) => {
  const response = await Api.put<UserActionResponse>(`/boletim_web/base/users/${id}`, payload);
  return response;
};

export const getUserFormOptions = async () => {
  const response = await Api.get<UserFormOptionsResponse>('/boletim_web/base/users/form_options');
  return response;
};

export interface UserInfoContactClient {
  nome?: string;
  email?: string;
  ddd_celular?: string;
  numero_celular?: string;
  ddd_celular_adicional1?: string;
  numero_celular_adicional1?: string;
  ddd_telefone?: string;
  numero_telefone?: string;
  ramal_telefone?: string;
  role?: { id: number; nome: string };
  genre?: { id: number; nome: string };
}

export interface UserInfoResponse {
  user: {
    id: number;
    nome: string;
    email: string;
    ativo: boolean;
    avatar_url?: string | null;
    contact_client?: UserInfoContactClient;
  };
  beta_tester: boolean;
  no_notification_bw: boolean;
  no_bulletin_push_notification: boolean;
  no_bulletin_email_notification: boolean;
  eligible_beta_tester_program: boolean;
}

export const getUserInfo = async (userId: number) => {
  const response = await Api.get<UserInfoResponse>('/boletim_web/minha_conta/get_user_info', {
    params: { user_id: userId },
  });
  return response;
};

export const createPreferenceForUser = async (preferenceId: number, userId: number) => {
  const response = await Api.post('/boletim_web/minha_conta/user_preferences', {
    preference_id: preferenceId,
    user_id: userId,
  });
  return response;
};

export const destroyPreferenceForUser = async (preferenceId: number, userId: number) => {
  const response = await Api.delete('/boletim_web/minha_conta/user_preferences', {
    params: { preference_id: preferenceId, user_id: userId },
  });
  return response;
};

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export const deactivateUser = async (userId: number) => {
  const response = await Api.post<UserActionResponse>(
    `/boletim_web/base/users/${userId}/deactivate`,
  );
  return response;
};

export const resetUserPassword = async (userId: number) => {
  const response = await Api.post<ResetPasswordResponse>(
    `/boletim_web/base/users/${userId}/reset_password`,
  );
  return response;
};

export interface AvatarUploadResponse {
  success: boolean;
  message: string;
  avatar_url?: string;
}

export const uploadUserAvatar = async (userId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await Api.post<AvatarUploadResponse>(
    `/boletim_web/base/users/${userId}/upload_avatar`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response;
};

export const destroyUserAvatar = async (userId: number) => {
  const response = await Api.delete<AvatarUploadResponse>(
    `/boletim_web/base/users/${userId}/destroy_avatar`,
  );
  return response;
};

export interface UserFilterEntry {
  id: number;
  name: string;
  has_access: boolean;
}

export interface UserFiltersResponse {
  success: boolean;
  filters: UserFilterEntry[];
}

export const getUserFilters = async (userId: number) => {
  const response = await Api.get<UserFiltersResponse>(`/boletim_web/base/users/${userId}/filters`);
  return response;
};

export const grantUserFilter = async (userId: number, filterId: number) => {
  const response = await Api.post(`/boletim_web/base/users/${userId}/grant_filter`, {
    filter_id: filterId,
  });
  return response;
};

export const revokeUserFilter = async (userId: number, filterId: number) => {
  const response = await Api.delete(`/boletim_web/base/users/${userId}/revoke_filter`, {
    data: { filter_id: filterId },
  });
  return response;
};

export const updateUserFilters = async (
  userId: number,
  grantIds: number[],
  revokeIds: number[],
) => {
  const response = await Api.post<{ success: boolean; message?: string }>(
    `/boletim_web/base/users/${userId}/update_filters`,
    { grant_ids: grantIds, revoke_ids: revokeIds },
  );
  return response;
};
